import { z } from 'zod';
import { productVariantExchangeResponse } from '../customer/schema/variantExchange.js';
import { claimAddress } from '../schema/address.js';
import { claim, claimCreate, claimCustomerPayment, claimFinalizePayload, claimList, claimListCount, claimListItem, claimNeedsMoreInfoPayload, claimNotifyByEmail, claimUpdateCustomerInfo, claimUpdateFees, tag, } from '../schema/claim.js';
import { approvedFinalizationLineItem, claimLineItem, claimLineItemInspection, claimLineItemInspectionCreate, claimLineItemUpdate, } from '../schema/claimLineItem.js';
import { claimReason, claimReasonCreate, claimReasonDetailUpdate, claimReasonGroup, claimReasonGroupCreate, claimReasonGroupUpdate, claimReasonUpdate, } from '../schema/claimReasons.js';
import { claimTimeline, claimTimelineEvent } from '../schema/claimTimeline.js';
import { crewOrder, orderListSchema, } from '../schema/order.js';
import { customField, customFieldCreate, customFieldUpdate, } from '../schema/customField.js';
import { pageInfo, reportRequestBody, reportRequestFormValues, searchFilterReq, storeUiView, viewDateValue, } from '../schema/filters.js';
import { claimInspectionSettings, inspectionTemplate, } from '../schema/inspection.js';
import { registration, registrationList, registrationUpdate, } from '../schema/registration.js';
import { returnLocation, returnLocationCreate, } from '../schema/returnLocation.js';
import { shipmentAdminCreate, shipmentAdminQuote, shipmentAdminQuoteAndBuy, shipmentAdminQuoteResponse, } from '../schema/shipment.js';
import { shippingClaimCreateMerchant, shippingClaimFinalize, shippingClaimList, shippingClaimMerchant, } from '../schema/shippingClaim/shippingClaim.js';
import { signedUrl } from '../schema/signedUrl.js';
import { productCollection, productTag, productType, storeProduct, storeProductGroup, storeProductGroupCreate, storeProductMinimum, } from '../schema/storeProduct.js';
import { arrayFactData, storeRule, storeRuleCreate, } from '../schema/storeRule.js';
import { superset } from '../schema/superset.js';
import { apiClientResp } from './error.js';
import { invoice } from './schema/invoices.js';
import { merchantOnboard } from './schema/merchantOnboard.js';
import { appSubscription, blueBoxUpdate, easyPostUpdate, rechargeUpdate, sendlaneUpdate, settingsConfig, settingsIntegration, settingsIntegrationUpdate, settingsTheme, shipBobUpdate, shipHeroUpdate, shippingProtection, shippingProviderUpdate, skioConfigUpdate, statusConfigUpdate, vesylUpdate, xbFulfillmentUpdate, } from './schema/settings.js';
import { storeCustomer } from './schema/storeCustomer.js';
import { Carrier as StoreShipRateCarrier, CarrierService as StoreShipRateCarrierService, protectionRate, protectionRateCreate, } from './schema/storeShipRate.js';
import { userAccessCodeStore } from './schema/storeWithUsers.js';
import { storeUser, storeUserCreate, storeUserSignUp, storeUserUpdate, userSignUp, userSignUpAndOnboard, userWithStoreUserRoles, } from './schema/user.js';
/**
 * CrewUi types for the crew-merchant-ui :)
 * Utility types to be used in conjunction with the relevant API endpoint types.
 */
// eslint-disable-next-line @typescript-eslint/no-namespace
export var CrewMerchantUi;
(function (CrewMerchantUi) {
    // ? we still want to be able to support that do not have a product / variant ID from Shopify, currently this is an error for us
    // ? urls being used by the API to redirect to the merchant-ui
    CrewMerchantUi.frontEndUrlPaths = [
        `:storeId/settings/integrations`, // ? used by the API to redirect to the merchant-ui after app subscription generation + stripe subscription
        ':storeId/claims/:claimType/:claimId', // ? used by the API to redirect to a specific claim in the merchant-ui
    ];
    // Shared Constants
    CrewMerchantUi.CREW_DISCOUNT_CODE = 'crew-discount';
    CrewMerchantUi.CREW_STORE_CREDIT_ORDER_PREFIX = '#C-GC-';
    CrewMerchantUi.MERCHANT_APP_JWT_QUERY_KEY = 'accessCode';
    CrewMerchantUi.invoiceSchema = invoice;
    // ----------- protection rates ------------
    CrewMerchantUi.Carrier = StoreShipRateCarrier;
    CrewMerchantUi.CarrierService = StoreShipRateCarrierService;
    CrewMerchantUi.protectionRateSchema = protectionRate;
    CrewMerchantUi.protectionRateUpdateSchema = protectionRate;
    CrewMerchantUi.protectionRateCreateSchema = protectionRateCreate;
    CrewMerchantUi.storeCustomerSchema = storeCustomer;
    CrewMerchantUi.userAccessCodeSchema = userAccessCodeStore;
    // ----------  App Subscription  ----------
    CrewMerchantUi.appSubscriptionSchema = appSubscription;
    CrewMerchantUi.userWithStoreUserRolesSchema = userWithStoreUserRoles;
    CrewMerchantUi.storeUserSchema = storeUser;
    CrewMerchantUi.storeUserCreateSchema = storeUserCreate;
    CrewMerchantUi.storeUserUpdateSchema = storeUserUpdate;
    CrewMerchantUi.signedUrlSchema = signedUrl;
    CrewMerchantUi.supersetTokenSchema = superset;
    CrewMerchantUi.returnLocationSchema = returnLocation;
    CrewMerchantUi.returnLocationCreateSchema = returnLocationCreate;
    // ---------- Store InspectionSettings   ----------
    CrewMerchantUi.inspectionTemplateSchema = inspectionTemplate;
    CrewMerchantUi.claimInspectionSettingsSchema = claimInspectionSettings;
    CrewMerchantUi.claimFinalizePayloadSchema = claimFinalizePayload;
    CrewMerchantUi.ClaimFinalizeApprovedLineItemSchema = approvedFinalizationLineItem;
    CrewMerchantUi.claimTimelineSchema = claimTimeline;
    CrewMerchantUi.claimTimelineEventSchema = claimTimelineEvent;
    CrewMerchantUi.claimNotifyByEmailSchema = claimNotifyByEmail;
    CrewMerchantUi.claimNeedsMoreInfoSchema = claimNeedsMoreInfoPayload;
    CrewMerchantUi.claimReasonSchema = claimReason;
    CrewMerchantUi.claimReasonCreateSchema = claimReasonCreate;
    CrewMerchantUi.claimReasonUpdateSchema = claimReasonUpdate;
    CrewMerchantUi.claimReasonDetailUpdateSchema = claimReasonDetailUpdate;
    CrewMerchantUi.claimReasonGroupSchema = claimReasonGroup;
    CrewMerchantUi.claimReasonGroupUpdateSchema = claimReasonGroupUpdate;
    CrewMerchantUi.claimReasonGroupCreateSchema = claimReasonGroupCreate;
    CrewMerchantUi.claimSchema = claim;
    CrewMerchantUi.claimUpdateCustomerInfoSchema = claimUpdateCustomerInfo;
    CrewMerchantUi.claimUpdateRefundHandlingFeeSchema = claimUpdateFees;
    CrewMerchantUi.claimTagSchema = tag;
    CrewMerchantUi.claimTagCreateSchema = tag.omit({ id: true });
    CrewMerchantUi.claimMinimumDataSchema = claimListItem;
    CrewMerchantUi.claimListSchema = claimList;
    CrewMerchantUi.claimListCountSchema = claimListCount;
    CrewMerchantUi.claimListItemSchema = claimListItem;
    CrewMerchantUi.claimLineItemSchema = claimLineItem;
    CrewMerchantUi.claimShipmentQuoteAndBuySchema = shipmentAdminQuoteAndBuy;
    CrewMerchantUi.claimReturnShipmentCreateSchema = shipmentAdminCreate;
    CrewMerchantUi.claimReturnShipmentQuoteSchema = shipmentAdminQuote;
    CrewMerchantUi.claimReturnShipmentQuoteResponse = shipmentAdminQuoteResponse;
    CrewMerchantUi.claimLineItemInspectionSchema = claimLineItemInspection;
    CrewMerchantUi.claimLineItemInspectionCreateSchema = claimLineItemInspectionCreate;
    CrewMerchantUi.claimAddressSchema = claimAddress;
    CrewMerchantUi.claimCustomerPaymentSchema = claimCustomerPayment;
    CrewMerchantUi.settingsConfigSchema = settingsConfig;
    CrewMerchantUi.settingsThemeSchema = settingsTheme;
    CrewMerchantUi.settingsIntegrationSchema = settingsIntegration;
    CrewMerchantUi.shipProtectSettingsSchema = shippingProtection;
    CrewMerchantUi.blueBoxUpdateSchema = blueBoxUpdate;
    CrewMerchantUi.shipBobUpdateSchema = shipBobUpdate;
    CrewMerchantUi.shipHeroUpdateSchema = shipHeroUpdate;
    CrewMerchantUi.skioConfigUpdateSchema = skioConfigUpdate;
    CrewMerchantUi.xBFulfillmentUpdateSchema = xbFulfillmentUpdate;
    CrewMerchantUi.sendlaneUpdateSchema = sendlaneUpdate;
    CrewMerchantUi.shippingProviderUpdateSchema = shippingProviderUpdate;
    CrewMerchantUi.vesylUpdateSchema = vesylUpdate;
    CrewMerchantUi.rechargeUpdateSchema = rechargeUpdate;
    CrewMerchantUi.easyPostUpdateSchema = easyPostUpdate;
    CrewMerchantUi.statusConfigUpdateSchema = statusConfigUpdate;
    CrewMerchantUi.settingsIntegrationUpdateSchema = settingsIntegrationUpdate;
    CrewMerchantUi.storeProductSchema = storeProduct;
    CrewMerchantUi.storeProductTypeSchema = productType;
    CrewMerchantUi.storeProductTagSchema = productTag;
    CrewMerchantUi.storeProductCollectionSchema = productCollection;
    CrewMerchantUi.storeProductGroupSchema = storeProductGroup;
    CrewMerchantUi.storeProductGroupCreateSchema = storeProductGroupCreate;
    CrewMerchantUi.storeProductsForGroupSchema = storeProductMinimum;
    CrewMerchantUi.storeProductGroupUpdateSchema = storeProductGroup;
    CrewMerchantUi.apiClientRespSchema = apiClientResp;
    CrewMerchantUi.merchantOnboardSchema = merchantOnboard;
    CrewMerchantUi.arrayFactDataSchema = arrayFactData;
    CrewMerchantUi.storeRuleSchema = storeRule;
    CrewMerchantUi.storeRuleUpdateSchema = storeRule;
    CrewMerchantUi.storeRuleCreateSchema = storeRuleCreate;
    CrewMerchantUi.customFieldSchema = customField;
    CrewMerchantUi.customFieldCreateSchema = customFieldCreate;
    CrewMerchantUi.customFieldUpdateSchema = customFieldUpdate;
    CrewMerchantUi.claimLineItemUpdateSchema = claimLineItemUpdate;
    CrewMerchantUi.crewOrderSchema = crewOrder;
    CrewMerchantUi.claimCreateSchema = claimCreate;
    CrewMerchantUi.variantExchangeOptionsSchema = productVariantExchangeResponse;
    CrewMerchantUi.shippingClaimSchema = shippingClaimMerchant;
    CrewMerchantUi.shippingClaimListSchema = shippingClaimList;
    CrewMerchantUi.shippingClaimFinalizeSchema = shippingClaimFinalize;
    CrewMerchantUi.shippingClaimCreateSchema = shippingClaimCreateMerchant;
    CrewMerchantUi.storeUserSignupCreateSchema = storeUserSignUp;
    CrewMerchantUi.storeUserSignUpSchema = userSignUp;
    CrewMerchantUi.storeUserSignUpAndOnboardSchema = userSignUpAndOnboard;
    CrewMerchantUi.registrationSchema = registration;
    CrewMerchantUi.registrationUpdateSchema = registrationUpdate;
    CrewMerchantUi.registrationListSchema = registrationList;
    CrewMerchantUi.searchReqSchema = searchFilterReq;
    CrewMerchantUi.searchRespSchema = z.discriminatedUnion('kind', [
        z.object({
            data: CrewMerchantUi.shippingClaimListSchema,
            kind: z.literal('Shipping'),
            pageInfo,
        }),
        z.object({
            data: CrewMerchantUi.registrationListSchema,
            kind: z.literal('Registration'),
            pageInfo,
        }),
        z.object({
            data: CrewMerchantUi.claimListSchema,
            kind: z.literal('Warranty'),
            pageInfo,
        }),
        z.object({
            data: CrewMerchantUi.claimListSchema,
            kind: z.literal('Return'),
            pageInfo,
        }),
        z.object({
            data: orderListSchema,
            kind: z.literal('Order'),
            pageInfo,
        }),
    ]);
    CrewMerchantUi.searchViewSchema = storeUiView;
    CrewMerchantUi.searchViewDateSchema = viewDateValue;
    CrewMerchantUi.reportReqSchema = reportRequestBody;
    CrewMerchantUi.reportRequestFormValueSchema = reportRequestFormValues;
})(CrewMerchantUi || (CrewMerchantUi = {}));
//# sourceMappingURL=schema.js.map